/**
 * A bottom sheet for the phone calendar: one task, burst or schedule in
 * words, with what this viewer may do to it.
 *
 * A real modal dialog: focus moves into it when it opens and stays there
 * (Tab wraps), Escape and the ✕ close it, a press on the scrim closes it,
 * and focus goes back to whatever opened it. The phone's Back closes it
 * too -- the screen pushes the open sheet as its own history entry
 * (`phone-url.ts`), so that is the router's work, not this file's.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function PhoneSheet({
  title,
  eyebrow,
  onClose,
  children,
  testId = 'pcal-sheet',
}: {
  title: string;
  eyebrow?: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Focus in on open; back to the opener on close.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => {
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (nodes.length === 0) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return createPortal(
    <div
      className="nx-pcal-sheet__scrim"
      data-nx-modal-layer=""
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="nx-pcal-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
      >
        <div className="nx-pcal-sheet__grip" aria-hidden="true" />
        <div className="nx-pcal-sheet__head">
          <div className="nx-pcal-sheet__titles">
            {eyebrow && <span className="nx-pcal-sheet__eyebrow">{eyebrow}</span>}
            <h2 id={titleId} className="nx-pcal-sheet__title" data-testid="pcal-sheet-title">
              {title}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="nx-pcal-sheet__close"
            onClick={onClose}
            aria-label="Close"
            data-testid="pcal-sheet-close"
          >
            ×
          </button>
        </div>
        <div className="nx-pcal-sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
